import { Group, Stack, Text } from '@mantine/core';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from 'react-map-gl';

import {
  getGesStatusI18nKey,
  parseGesStatus,
} from '../utils/ges-status';

export interface ResultsFieldPopupData {
  longitude: number;
  latitude: number;
  name?: string | null;
  color?: string | null;
  code?: string | null;
  farm?: { id: number; name: string } | null;
  campaign?: { id: number; name: string; year?: number } | null;
  ges_status?: number | null;
  csr_year_reference?: string | number | null;
  csa_year_reference?: string | number | null;
  csr_carbon_content?: number | null;
  csr_soil_density?: number | null;
  csr_carbon_stock?: number | null;
  csr_lower_carbon_stock_threshold?: number | null;
  csr_upper_carbon_stock_threshold?: number | null;
  csr_median_carbon_stock?: number | null;
  csa_carbon_content?: number | null;
  csa_soil_density?: number | null;
  csa_carbon_stock?: number | null;
  csa_lower_carbon_stock_threshold?: number | null;
  csa_upper_carbon_stock_threshold?: number | null;
  csa_median_carbon_stock?: number | null;
}

interface ResultsFieldPopupProps {
  data: ResultsFieldPopupData | null;
  onClose: () => void;
}

function formatNum(value: number | null | undefined): string {
  if (value == null) return '-';
  return Number(value).toFixed(2).replace('.', ',');
}

function formatMetric(value: number | null | undefined, unit: string): string {
  return value == null ? '-' : `${formatNum(value)} ${unit}`;
}

function formatRange(
  lower: number | null | undefined,
  upper: number | null | undefined,
): string {
  if (lower == null || upper == null) return '-';
  return `${formatNum(lower)} - ${formatNum(upper)} tC/ha`;
}

function MetricLine({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text c="dimmed" size="xs">
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

function MetricLineBoldValue({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text c="dimmed" fw={700} size="xs">
        {label}
      </Text>
      <Text fw={700} size="sm">
        {value}
      </Text>
    </Group>
  );
}

export const ResultsFieldPopup: FC<ResultsFieldPopupProps> = ({ data, onClose }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const gesStatus = parseGesStatus(data.ges_status);

  return (
    <Popup
      anchor="bottom"
      closeOnClick={false}
      closeOnMove={false}
      latitude={data.latitude}
      longitude={data.longitude}
      maxWidth="460px"
      offset={[0, -15]}
      onClose={onClose}
    >
      <Stack gap="xs" maw={420} miw={360}>
        {data.name != null && data.name !== '' && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.parcelle')}
              </Text>
              <Text size="sm">{data.name}</Text>
            </div>
          </Group>
        )}

        {data.farm?.name != null && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.societe')}
              </Text>
              <Text size="sm">{data.farm.name}</Text>
            </div>
          </Group>
        )}

        {data.campaign != null && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.campaign')}
              </Text>
              <Text size="sm">{data.campaign.name}</Text>
            </div>
          </Group>
        )}

        {data.code != null && data.code !== '' && (
          <Group align="flex-start" gap="xs">
            <div>
              <Text c="dimmed" fw={500} size="xs" tt="uppercase">
                {t('common.code')}
              </Text>
              <Text size="sm">{data.code}</Text>
            </div>
          </Group>
        )}

        <Group align="flex-start" gap="xs">
          <div>
            <Text c="dimmed" fw={500} size="xs" tt="uppercase">
              {t('table.columns.status')}
            </Text>
            <Text c="dimmed" mt={4} size="xs">
              {gesStatus != null
                ? t(getGesStatusI18nKey(gesStatus))
                : '-'}
            </Text>
          </div>
        </Group>

        <Stack gap={4}>
          <Text fw={600} size="sm">
            {`CSR ${data.csr_year_reference ?? '-'}`}
          </Text>
          <MetricLine
            label={t('common.densite-du-sol')}
            value={formatMetric(data.csr_soil_density, 'g/cm³')}
          />
          <MetricLine
            label={t('common.taux-carbone')}
            value={formatMetric(data.csr_carbon_content, 'g/kg')}
          />
          <MetricLine
            label={t('common.carbon_stock')}
            value={formatMetric(data.csr_carbon_stock, 'tC/ha')}
          />
          <MetricLine
            label={t('common.median_carbon_stock')}
            value={formatMetric(data.csr_median_carbon_stock, 'tC/ha')}
          />
          <MetricLineBoldValue
            label={`${t('common.lower_carbon_stock_threshold')} - ${t('common.upper_carbon_stock_threshold')}`}
            value={formatRange(
              data.csr_lower_carbon_stock_threshold,
              data.csr_upper_carbon_stock_threshold,
            )}
          />
        </Stack>

        <Stack gap={4}>
          <Text fw={600} size="sm">
            {`CSA ${data.csa_year_reference ?? '-'}`}
          </Text>
          <MetricLine
            label={t('common.densite-du-sol')}
            value={formatMetric(data.csa_soil_density, 'g/cm³')}
          />
          <MetricLine
            label={t('common.taux-carbone')}
            value={formatMetric(data.csa_carbon_content, 'g/kg')}
          />
          <MetricLine
            label={t('common.median_carbon_stock')}
            value={formatMetric(data.csa_median_carbon_stock, 'tC/ha')}
          />
          <MetricLine
            label={t('common.carbon_stock')}
            value={formatMetric(data.csa_carbon_stock, 'tC/ha')}
          />
          <MetricLineBoldValue
            label={`${t('common.lower_carbon_stock_threshold')} - ${t('common.upper_carbon_stock_threshold')}`}
            value={formatRange(
              data.csa_lower_carbon_stock_threshold,
              data.csa_upper_carbon_stock_threshold,
            )}
          />
        </Stack>

      </Stack>
    </Popup>
  );
};
